import React, {SetStateAction, useCallback, useEffect, useMemo, useRef, useState, useContext} from 'react'
import { AllyContextType, Summary } from '../types/ally'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { GlobalUser } from '../types/common.types'
import * as CommonTypes from '../types/common.types'
import { startOfToday } from 'date-fns'
import AllyService from '../services/ally.service'
import toQuery from '../utils/toQuery'

const AllyProvider = React.createContext<AllyContextType>(null as any)

export const AllyContext = ({children}: {children: React.ReactNode}) => {
    const router = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    
    const getUser = () => {
        const user = localStorage.getItem('ally')
        if (user) {
            return JSON.parse(user)
        } else return null
    }
    
    // Refs to track update sources
    const isUpdatingFromUrl = useRef(false);
    const initialLoadDone = useRef(false);
    const prevSearchParamsString = useRef(searchParams.toString());
    const prevPathname = useRef(location.pathname);
    
    // Helper function to validate date string
    const isValidDateString = (dateString: string | null): boolean => {
        if (!dateString) return false;
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    };

    // useStates
    const [user, setUser] = useState<GlobalUser>(getUser())
    const [tripInfo, setTripInfo] = useState<CommonTypes.FullReservation | undefined>()
    const [newReservationData, setNewReservationData] = useState<Partial<CommonTypes.Reservation> | undefined>()
    const [isPassengersModalOpen, setIsPassengersModalOpen] = useState<boolean>(false)
    const [allyInfo, setAllyInfo] = useState<CommonTypes.HealthAlly>({} as CommonTypes.HealthAlly)
    

    const [editReservationId, setEditReservationId] = useState<string>('')
    const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState<boolean>(false)
    const [newGroup, setNewGroup] = useState<Partial<CommonTypes.Passenger>[]>([])
    const [newGroupReservationData, setNewGroupReservationData] = useState<Partial<CommonTypes.Reservation> | undefined>()
    const [isTicketPaymentGroupModalOpen, setIsTicketPaymentGroupModalOpen] = useState<boolean>(false)
    const [isConfirmBoucherModalOpen, setIsConfirmBoucherModalOpen] = useState<boolean>(false)
    const [newGroupReservations, setNewGroupReservations] = useState<CommonTypes.Reservation[]>([])
    const [summary, setSummary] = useState<Summary[]>([])
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState<boolean>(false)
    const [confirmDeleteReservationId, setConfirmDeleteReservationId] = useState<string>('')
    const [viewPaymentsData, setViewPaymentsData] = useState<CommonTypes.ReservationPayment[]>()
    const [reservationData, setReservationData] = useState<CommonTypes.ReservationPaymentList | undefined>()
    const [createPaymentReservationId, setCreatePaymentReservationId] = useState<string>('')
    const [createPaymentGroupId, setCreatePaymentGroupId] = useState<string>('')
    const [isCreatePaymentModalOpen, setIsCreatePaymentModalOpen] = useState<boolean>(false)
        
    // Initialize currentDay from URL
    const [currentDay, setCurrentDay] = useState<Date>(() => {
        const urlDay = searchParams.get('currentDay');
        console.log('Initializing currentDay from URL:', urlDay);
        
        if (urlDay && isValidDateString(urlDay)) {
            try {
                const date = new Date(urlDay);
                if (!isNaN(date.getTime())) {
                    console.log('Parsed currentDay:', date);
                    return date;
                }
            } catch (e) {
                console.error('Error parsing currentDay:', e);
            }
        }
        
        console.log('Using today as fallback for currentDay');
        return startOfToday();
    });
        
    // Initialize endDay from URL
    const [endDay, setEndDay] = useState<Date | undefined>(() => {
        const urlDay = searchParams.get('endDay');
        console.log('Initializing endDay from URL:', urlDay);
        
        if (urlDay && isValidDateString(urlDay)) {
            try {
                const date = new Date(urlDay);
                if (!isNaN(date.getTime())) {
                    console.log('Parsed endDay:', date);
                    return date;
                }
            } catch (e) {
                console.error('Error parsing endDay:', e);
            }
        }
        
        return undefined;
    });
    
    user?.token && AllyService.setToken(user.token)

    const handleSetCurrentDay = useCallback((value: SetStateAction<Date>) => {
        if (!isUpdatingFromUrl.current) {
            setCurrentDay(value);
        }
    }, []);
        
    const handleSetEndDay = useCallback((value: SetStateAction<Date | undefined>) => {
        if (!isUpdatingFromUrl.current) {
            setEndDay(value);
        }
    }, []);

    // Sync URL to state when URL changes
    useEffect(() => {
        if (!user) return;
        
        const currentPathname = location.pathname;
        const currentParamsString = searchParams.toString();
        const isOnMeRoute = currentPathname === '/seller/me';
        const wasOnMeRoute = prevPathname.current === '/seller/me';
        
        // Handle route changes
        if (currentPathname !== prevPathname.current) {
            console.log('Route changed to:', currentPathname);
            
            // If we're leaving the /seller/me route, don't sync sellerTab from URL
            if (wasOnMeRoute && !isOnMeRoute) {
                prevPathname.current = currentPathname;
                prevSearchParamsString.current = currentParamsString;
                return;
            }
        }
        
        // Skip if this is the initial load
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            prevPathname.current = currentPathname;
            prevSearchParamsString.current = currentParamsString;
            return;
        }
        
        // Skip if URL hasn't changed
        if (currentParamsString === prevSearchParamsString.current && currentPathname === prevPathname.current) {
            return;
        }
        
        console.log('URL changed, syncing to state:', currentParamsString);
        isUpdatingFromUrl.current = true;
        
        // Only sync sellerTab if on /seller/me route
        if (isOnMeRoute) {
            const urlSellerTab = searchParams.get('sellerTab');
            // if (urlSellerTab && urlSellerTab !== sellerTab) {
            //     setSellerTab(urlSellerTab);
            // }
        }
        
        // Update currentDay from URL
        const urlCurrentDay = searchParams.get('currentDay');
        if (urlCurrentDay) {
            try {
                const newDate = new Date(urlCurrentDay);
                if (!isNaN(newDate.getTime())) {
                    setCurrentDay(prev => {
                        if (prev.getTime() !== newDate.getTime()) {
                            return newDate;
                        }
                        return prev;
                    });
                }
            } catch (e) {
                console.error('Error parsing currentDay from URL:', e);
            }
        }
        
        // Update endDay from URL
        const urlEndDay = searchParams.get('endDay');
        if (urlEndDay) {
            try {
                const newDate = new Date(urlEndDay);
                if (!isNaN(newDate.getTime())) {
                    setEndDay(prev => {
                        if (!prev || prev.getTime() !== newDate.getTime()) {
                            return newDate;
                        }
                        return prev;
                    });
                }
            } catch (e) {
                console.error('Error parsing endDay from URL:', e);
            }
        } else {
            setEndDay(undefined);
        }
        
        prevPathname.current = currentPathname;
        prevSearchParamsString.current = currentParamsString;
        
        // Reset the flag after state updates are processed
        Promise.resolve().then(() => {
            isUpdatingFromUrl.current = false;
        });
        
    }, [searchParams, location.pathname, user]);

    // Sync state to URL - debounced
    useEffect(() => {
        if (!user || !initialLoadDone.current || isUpdatingFromUrl.current) return;

        const currentPathname = location.pathname;
        const isOnMeRoute = currentPathname === '/seller/me';

        const timeoutId = setTimeout(() => {
        const params: Record<string, string> = {};

        // Only add sellerTab to URL if on /seller/me route
        if (isOnMeRoute) {
            // params.sellerTab = sellerTab;
        }

        // Add currentDay if it exists
        if (currentDay) {
            params.currentDay = currentDay.toISOString();
        }

        // Add endDay if it exists
        if (endDay) {
            params.endDay = endDay.toISOString();
        }

        const currentParams = searchParams.toString();
        const newParams = toQuery(params).replace('?', '');

        // Only update if params actually changed
            if (currentParams !== newParams) {
                console.log('Syncing state to URL:', newParams);
                router(`${currentPathname}${toQuery(params)}`);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [currentDay, endDay, router, user, searchParams, location.pathname])

    // Memoize the context value
    const values = useMemo(() => ({
        user, setUser,
        currentDay, setCurrentDay: handleSetCurrentDay,
        endDay, setEndDay: handleSetEndDay,
        tripInfo, setTripInfo,
        newReservationData, setNewReservationData,
        editReservationId, setEditReservationId,
        isCreateGroupModalOpen, setIsCreateGroupModalOpen,
        newGroup, setNewGroup,  
        newGroupReservationData, setNewGroupReservationData,    
        isTicketPaymentGroupModalOpen, setIsTicketPaymentGroupModalOpen,
        isConfirmBoucherModalOpen, setIsConfirmBoucherModalOpen,
        newGroupReservations, setNewGroupReservations,
        summary, setSummary,
        isSummaryModalOpen, setIsSummaryModalOpen,
        confirmDeleteReservationId, setConfirmDeleteReservationId,
        viewPaymentsData, setViewPaymentsData,
        reservationData, setReservationData,
        createPaymentReservationId, setCreatePaymentReservationId,
        isCreatePaymentModalOpen, setIsCreatePaymentModalOpen,
        createPaymentGroupId, setCreatePaymentGroupId,
        isPassengersModalOpen, setIsPassengersModalOpen,
        allyInfo, setAllyInfo
    }), [
        user, currentDay, endDay, tripInfo, newReservationData,
        editReservationId, isCreateGroupModalOpen, newGroup,
        newGroupReservationData, isTicketPaymentGroupModalOpen,
        isConfirmBoucherModalOpen, newGroupReservations, summary,
        isSummaryModalOpen, confirmDeleteReservationId, viewPaymentsData,
        reservationData, createPaymentReservationId, isCreatePaymentModalOpen,
        createPaymentGroupId, handleSetCurrentDay, handleSetEndDay, isPassengersModalOpen,
        allyInfo
    ]);

    return <AllyProvider.Provider value={values}>{children}</AllyProvider.Provider>;
}

export const useAlly = () => {
    return useContext(AllyProvider)
}